import { Box, Grid, Typography } from '@mui/material';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import PaymentButton from '@acorn-portal/shared/src/components/buttons/PaymentButton';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { handTakeUpMidTermAdjustment } from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { useNavigate } from 'react-router';
import {
	PaymentMethods,
	ExistingCardPaymentMethod,
	MtaPaymentCollectionOption,
} from '@acorn-portal/shared/src/services/api';
import { flipToPositive, formatPounds } from '@acorn-portal/shared/src/utils';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import ExistingCard from '@acorn-portal/shared/src/components/payment/ExistingCard';
import PriceBreakdown from '@acorn-portal/shared/src/components/display/price-breakdown';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { getPhoneNumber } from '@acorn-portal/shared/src/utils/get-phone-numbers';

interface IProps {
	toggleModal: (open: boolean) => void;
	setLoading: (loading: boolean) => void;
	refundOption: MtaPaymentCollectionOption;
	refundCard: ExistingCardPaymentMethod | undefined;
}

const CardRefundOption = ({ toggleModal, setLoading, refundOption, refundCard }: IProps) => {
	const midTermAdjustment = useAppSelector((redux) => redux.midTermAdjustment);
	const policy = useAppSelector((redux) => redux.fullPolicy);
	const contactPreferences = useAppSelector((redux) => redux.contactPreferences);
	const midTermAdjustmentResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);
	const navigate = useNavigate();
	const paymentPhoneNumber = getPhoneNumber(policy.product, 'CONTACT');

	const submitPaymentForm = async () => {
		setLoading(true);
		try {
			await handTakeUpMidTermAdjustment({
				policyReference: midTermAdjustment.policyReference,
				sendPostalDocuments: contactPreferences.printToPost,
				transactionId: midTermAdjustment.transactionId,
				takeUpDetails: {
					underwritingResponseId: midTermAdjustmentResponse.underwritingOutcomeResponseId,
					underwritingOptionId: midTermAdjustmentResponse.optionId,
					paymentCollectionOptionCode: refundOption.code,
					selectedBalanceSettlementMethod: PaymentMethods.CARD_PAYMENT,
				},
			});
			setLoading(false);
			navigate(ROUTE_PATHS.makeAChangePaymentSuccess);
		} catch (error) {
			console.error('Error processing payment:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	return (
		<>
			<Box>
				<Grid
					container
					maxWidth='md'
					margin='auto'
					sx={{
						border: 1,
						borderColor: 'grey.300',
						p: 2,
						mb: 4,
						borderRadius: 2,
					}}
				>
					<Grid container sx={{ flexDirection: 'column' }}>
						<Grid sx={{ textAlign: { xs: 'left' } }}>
							<Typography component='h3' color='primary'>
								Single Card Payment
							</Typography>
							<Typography fontSize={44} fontWeight='bold' color='primary' lineHeight={1}>
								{formatPounds(flipToPositive(refundOption.totalAmountPayable ?? 0))}
							</Typography>
							<Grid flex={1}>
								<Typography fontSize={16}>Refund Amount</Typography>
							</Grid>

							<PriceBreakdown
								price={refundOption.totalAmountPayable}
								fee={midTermAdjustmentResponse.coverDetails.fee}
								product={policy.product}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Box>
			<Box
				maxWidth='md'
				margin='auto'
				sx={(theme) => ({
					paddingTop: 3,
					paddingBottom: 3,
					marginTop: 2,
					marginBottom: 2,
					borderRadius: 2,
					border: `1px solid ${theme.palette.divider}`,
					backgroundColor: `${theme.palette.background.paper}`,
					boxShadow: `0 4px 12px ${theme.palette.primary.main}10`,
					padding: 3,
					position: 'relative',
					'&::before': {
						content: '""',
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '4px',
						background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
						borderTopLeftRadius: 8,
						borderTopRightRadius: 8,
					},
				})}
			>
				<Box
					sx={{
						border: 1,
						borderColor: 'grey.300',
						p: 2,
						mb: 4,
						borderRadius: 2,
					}}
				>
					{refundCard ? (
						<>
							<Typography>
								{formatPounds(flipToPositive(refundOption.totalAmountPayable ?? 0))} will be refunded to the card below:
							</Typography>
							<ExistingCard card={refundCard} />
						</>
					) : (
						<Typography>
							{formatPounds(flipToPositive(refundOption.totalAmountPayable ?? 0))} will be refunded to the card(s) you
							used to purchase the policy.
						</Typography>
					)}
					<Typography>
						If you need to change this card please call us on{' '}
						<Typography component='a' href={`tel:${paymentPhoneNumber}`} sx={{ color: 'primary.main' }}>
							{paymentPhoneNumber}
						</Typography>
					</Typography>
				</Box>
				<Box sx={{ margin: 4 }} />
				<ActionButtons>
					<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
					<PaymentButton onClick={submitPaymentForm} icon='Card'>
						Process Refund
					</PaymentButton>
				</ActionButtons>
			</Box>
		</>
	);
};

export default CardRefundOption;
